import React, {useMemo} from "react";
import Styles from './styles.module.scss';
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {formatNumber} from "../cabinetShell/config";

import heroArt from '../../theme/assets/redesign/settings/hero.webp';

const TOTAL_LAPS = 15;

const normStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (['completed', 'closed', 'finished', 'done'].includes(v)) return 'completed';
    if (['active', 'current', 'open'].includes(v)) return 'active';
    return 'upcoming';
};

const formatPrice = (v) => {
    const n = Number(v);
    if (!n && n !== 0) return '—';
    return `$${n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4})}`;
};


// Дорожная карта: 15 инвестиционных кругов (в макете страницы нет — собрано на дизайн-системе кабинета)
const CabinetRoadmapMain = () => {
    const {t} = useTranslation();
    const roadmap = useSelector(store => store.cabinet.roadmap);
    const roadmapStarted = useSelector(store => store.cabinet.roadmapStarted);

    const laps = useMemo(() => {
        const list = Array.isArray(roadmap) ? roadmap : (Array.isArray(roadmap?.data) ? roadmap.data : []);
        return list.map((item, i) => {
            const prev = i > 0 ? Number(list[i - 1]?.token_rate) : null;
            const rate = Number(item?.token_rate);
            return {
                index: i + 1,
                rate,
                status: normStatus(item?.status),
                growth: prev && rate ? Math.round(((rate - prev) / prev) * 100) : null,
            };
        });
    }, [roadmap]);

    const total = laps.length || TOTAL_LAPS;
    const completed = laps.filter(l => l.status === 'completed').length;
    const active = laps.find(l => l.status === 'active');
    const next = active ? laps.find(l => l.index === active.index + 1) : laps.find(l => l.status === 'upcoming');
    const progress = Math.min(100, Math.round(((completed + (active ? 0.5 : 0)) / total) * 100));
    const statusLabel = {
        completed: t('Завершён'),
        active: t('Активный'),
        upcoming: t('Скоро'),
    };

    return (
        <div className={Styles.container}>
            {/* ===== Hero ===== */}
            <section className={Styles.hero}>
                <div className={Styles.heroArt}><img src={heroArt} alt=""/></div>
                <div className={Styles.heroTitle}>{t('Дорожная карта')}</div>
                <p className={Styles.heroText}>
                    {t('Добро пожаловать в систему из 15 инвестиционных кругов — продуманный и открытый путь роста нашего токена. Каждый круг — это шаг вперёд: фиксированная цена, понятный объём эмиссии и чёткие правила, доступные каждому участнику. Ты можешь в любой момент видеть, на каком этапе находится проект, сколько токенов выпущено, какой спрос, и какие возможности открываются прямо сейчас')}.
                </p>
            </section>

            {/* ===== Прогресс ===== */}
            <section className={Styles.summary}>
                <div className={Styles.summaryStats}>
                    <div className={Styles.stat}>
                        <span>{t('Пройдено кругов')}</span>
                        <b>{completed}<i>/{total}</i></b>
                    </div>
                    <div className={Styles.stat}>
                        <span>{t('Текущий круг')}</span>
                        <b className={Styles.red}>{active ? `#${active.index}` : '—'}</b>
                    </div>
                    <div className={Styles.stat}>
                        <span>{t('Текущая цена')}</span>
                        <b>{active ? formatPrice(active.rate) : '—'}</b>
                    </div>
                    <div className={Styles.stat}>
                        <span>{t('Следующая цена')}</span>
                        <b className={Styles.green}>{next ? formatPrice(next.rate) : '—'}</b>
                    </div>
                </div>
                <div className={Styles.track}>
                    <div className={Styles.trackFill} style={{width: `${progress}%`}}/>
                </div>
            </section>

            {/* ===== Круги ===== */}
            <section className={Styles.panel}>
                <div className={Styles.panelTitle}>{t('Инвестиционные круги')}</div>
                {laps.length === 0 ? (
                    <div className={Styles.empty}>{roadmapStarted ? t('Загрузка...') : t('Данные о кругах пока недоступны')}</div>
                ) : (
                    <div className={Styles.grid}>
                        {laps.map(lap => (
                            <div key={lap.index} className={`${Styles.lap} ${Styles[lap.status]}`}>
                                <div className={Styles.lapTop}>
                                    <span className={Styles.lapName}>{t('Круг')} #{lap.index}</span>
                                    <span className={Styles.lapChip}>{statusLabel[lap.status]}</span>
                                </div>
                                <div className={Styles.lapLabel}>{t('Цена токена')}</div>
                                <div className={Styles.lapPrice}>{formatPrice(lap.rate)}</div>
                                <div className={Styles.lapGrowth}>
                                    {lap.growth !== null ? `+${formatNumber(lap.growth, 0)}% ${t('к прошлому кругу')}` : t('Стартовая цена')}
                                </div>
                            </div>
                        ))}
                        <div className={Styles.listing}>
                            <div className={Styles.listingIcon}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M4 22V15M4 15V4C4 4 5 3 8 3C11 3 13 5 16 5C19 5 20 4 20 4V15C20 15 19 16 16 16C13 16 11 14 8 14C5 14 4 15 4 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className={Styles.listingText}>
                                <span>{t('Листинг на бирже')}</span>
                                <span>{t('Итоговый результат')}</span>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default CabinetRoadmapMain;
